import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Baby,
  Scale,
  Clock,
  MapPin,
  CheckCircle,
  Smile,
  Shield,
  Heart,
  ShoppingBag
} from 'lucide-react';
import { pricingPlans, faqItems } from '../data/mock';

const ForParentsPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden jolly-hero-gradient py-16 md:py-24">
        <div className="absolute top-10 right-10 w-20 h-20 rounded-full bg-[hsl(45,98%,80%)] opacity-50 sun-pulse" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold">
                <span className="jolly-text-teal">Shopping Just Got</span>
                <br />
                <span className="jolly-text-orange">Easier.</span>
              </h1>
              <p className="text-gray-600 text-lg md:text-xl max-w-xl">
                Let your little one enjoy the ride while you enjoy stress-free shopping. Our car-shaped trolleys keep kids entertained and safe.
              </p>
              <div className="flex flex-wrap gap-4 justify-center lg:justify-start">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Baby className="w-5 h-5 jolly-text-orange" />
                  <span className="text-sm font-medium">Ages 1-6</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Scale className="w-5 h-5 jolly-text-blue" />
                  <span className="text-sm font-medium">Max 30kg</span>
                </div>
              </div>
            </div>
            
            {/* Illustration */}
            <div className="relative">
              <div className="bg-gradient-to-br from-[hsl(45,90%,85%)] to-[hsl(35,80%,80%)] rounded-3xl p-8 shadow-xl">
                <div className="text-center">
                  <div className="inline-block">
                    {/* Happy parent and child illustration */}
                    <div className="flex items-end justify-center gap-2">
                      <div className="text-6xl">👩‍👦</div>
                      <div className="w-24 h-16 bg-[hsl(210,70%,25%)] rounded-xl relative">
                        <div className="absolute inset-2 bg-[hsl(200,70%,80%)] rounded-lg flex items-center justify-center">
                          <span className="text-2xl">😄</span>
                        </div>
                        <div className="absolute -bottom-2 left-2 w-4 h-4 bg-gray-700 rounded-full" />
                        <div className="absolute -bottom-2 right-2 w-4 h-4 bg-gray-700 rounded-full" />
                      </div>
                    </div>
                  </div>
                  <p className="mt-4 text-lg font-bold jolly-text-teal">Happy Kids, Happy Parents!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits for Parents */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Why Parents Love Us</h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="jolly-card">
              <CardContent className="p-8 text-center">
                <div className="feature-icon mx-auto mb-4 bg-[hsl(0,70%,95%)]">
                  <Heart className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-xl font-bold jolly-text-teal mb-3">Less Stress</h3>
                <p className="text-gray-600 text-sm">
                  No more chasing toddlers through aisles or dealing with meltdowns. Your child stays safely contained and entertained.
                </p>
              </CardContent>
            </Card>
            
            <Card className="jolly-card">
              <CardContent className="p-8 text-center">
                <div className="feature-icon mx-auto mb-4 bg-[hsl(120,50%,92%)]">
                  <Shield className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold jolly-text-teal mb-3">Safer Shopping</h3>
                <p className="text-gray-600 text-sm">
                  Our 5-point harness keeps your child secure. You can shop with confidence knowing they're safe and visible.
                </p>
              </CardContent>
            </Card>
            
            <Card className="jolly-card">
              <CardContent className="p-8 text-center">
                <div className="feature-icon mx-auto mb-4 bg-[hsl(45,100%,90%)]">
                  <Smile className="w-8 h-8 jolly-text-orange" />
                </div>
                <h3 className="text-xl font-bold jolly-text-teal mb-3">Happier Kids</h3>
                <p className="text-gray-600 text-sm">
                  Kids love our car-shaped trolleys! Shopping becomes an exciting adventure rather than a boring chore.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Simple, Transparent Pricing</h2>
            <p className="text-gray-600 text-lg">Pay only for the time you need</p>
          </div>
          
          <Card className="overflow-hidden shadow-xl">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[hsl(195,80%,25%)] text-white">
                      <th className="py-4 px-6 text-left font-bold">Duration</th>
                      <th className="py-4 px-6 text-right font-bold">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingPlans.map((plan, index) => (
                      <tr 
                        key={plan.duration} 
                        className={`border-b last:border-b-0 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${plan.duration === 'Extra hour' ? 'bg-[hsl(45,100%,95%)]' : ''}`}
                      >
                        <td className="py-4 px-6">
                          <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 jolly-text-orange" />
                            <span className="font-medium text-gray-700">{plan.duration}</span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-right">
                          <span className="text-xl font-bold jolly-text-teal">{plan.price}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          
          <div className="mt-8 text-center">
            <p className="text-gray-600 text-sm mb-4">
              Payment accepted at the Jolly Bay station. Cash and card accepted.
            </p>
            <Link to="/contact">
              <Button className="jolly-btn-orange">
                <MapPin className="w-5 h-5 mr-2" />
                Find a Jolly Tots Ride Near You
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Rules Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">A Few Things to Know</h2>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-4 bg-[hsl(45,100%,96%)] rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Age Requirement</h4>
                <p className="text-gray-600 text-sm">Children must be between 1-6 years old</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-[hsl(45,100%,96%)] rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Weight Limit</h4>
                <p className="text-gray-600 text-sm">Maximum weight capacity is 30kg</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-[hsl(45,100%,96%)] rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Supervision Required</h4>
                <p className="text-gray-600 text-sm">Parent/guardian must remain with the trolley</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-[hsl(45,100%,96%)] rounded-xl">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-800 mb-1">Valid ID</h4>
                <p className="text-gray-600 text-sm">ID or driver's license required for rental</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 jolly-gradient-bg">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="jolly-section-title">Frequently Asked Questions</h2>
          </div>
          
          <Accordion type="single" collapsible className="space-y-4">
            {faqItems.map((item) => (
              <AccordionItem 
                key={item.id} 
                value={`item-${item.id}`}
                className="bg-white rounded-xl px-6 shadow-sm border-none"
              >
                <AccordionTrigger className="text-left font-semibold jolly-text-teal hover:no-underline py-5">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 pb-5">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-[hsl(33,100%,50%)]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready for a Stress-Free Shopping Trip?
          </h2>
          <p className="text-white/90 text-lg mb-8">
            Visit any participating mall and look for the Jolly Bay station!
          </p>
          <Link to="/contact">
            <Button className="bg-white text-[hsl(33,100%,40%)] hover:bg-gray-100 font-bold rounded-full px-8 py-4 text-base shadow-lg">
              <ShoppingBag className="w-5 h-5 mr-2" />
              Find a Location
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForParentsPage;
